import React from 'react';

import DonutPagesChart from '../components/DonutPagesChart';



type Props = {
	data: Array<{
		count: number;
		percentage: number;
		value: string;
	}>;
};



const StatusCodeChart: React.FC<Props> = (props) => {
	const {
		data,
	} = props;

	const createBracketsData = (data: Props['data']) => {
		const dataPerBracket = {
			'2xx': 0,
			'3xx': 0,
			'4xx': 0,
			'5xx': 0,
		};

		let total = 0;
		data.forEach((item) => {
			let bracket: keyof typeof dataPerBracket;

			if (Number(item.value) >= 200 && Number(item.value) < 300) {
				bracket = '2xx';
			} else if (Number(item.value) >= 300 && Number(item.value) < 400) {
				bracket = '3xx';
			} else if (Number(item.value) >= 400 && Number(item.value) < 500) {
				bracket = '4xx';
			} else if (Number(item.value) >= 500) {
				bracket = '5xx';
			} else {
				return;
			}

			total += item.count;
			dataPerBracket[bracket] += item.count;
		});

		return [
			{
				value: '2xx',
				count: dataPerBracket['2xx'],
				percentage: dataPerBracket['2xx'] / total * 100,
			},
			{
				value: '3xx',
				count: dataPerBracket['3xx'],
				percentage: dataPerBracket['3xx'] / total * 100,
			},
			{
				value: '4xx',
				count: dataPerBracket['4xx'],
				percentage: dataPerBracket['4xx'] / total * 100,
			},
			{
				value: '5xx',
				count: dataPerBracket['5xx'],
				percentage: dataPerBracket['5xx'] / total * 100,
			},
		].filter((item) => item.count > 0);
	};

	const useBrackets = data.length > 6;
	const chartData = useBrackets ? createBracketsData(data) : data;

	const categories = React.useMemo(
		() => {
			const bracketCategories = [
				{
					value: '2xx',
					label: '2xx',
					color: '#42CC67',
				},
				{
					value: '3xx',
					label: '3xx',
					color: '#367be2',
				},
				{
					value: '4xx',
					label: '4xx',
					color: '#FF5959',
				},
				{
					value: '5xx',
					label: '5xx',
					color: '#FE8719',
				},
			];

			const specificCategories = [
				{
					value: 200,
					label: '200',
					color: '#42CC67',
				},
				{
					value: 201,
					label: '201',
					color: 'rgba(66,204,103,.95)',
				},
				{
					value: 202,
					label: '202',
					color: 'rgba(66,204,103,.9)',
				},
				{
					value: 203,
					label: '203',
					color: 'rgba(66,204,103,.85)',
				},
				{
					value: 204,
					label: '204',
					color: 'rgba(66,204,103,.8)',
				},
				{
					value: 205,
					label: '205',
					color: 'rgba(66,204,103,.75)',
				},
				{
					value: 206,
					label: '206',
					color: 'rgba(66,204,103,.7)',
				},
				{
					value: 207,
					label: '207',
					color: 'rgba(66,204,103,.65)',
				},
				{
					value: 208,
					label: '208',
					color: 'rgba(66,204,103,.6)',
				},
				{
					value: 226,
					label: '226',
					color: 'rgba(66,204,103,.55)',
				},
				{
					value: 300,
					label: '300',
					color: 'rgba(23,161,229,.4)',
				},
				{
					value: 301,
					label: '301',
					color: '#367be2',
				},
				{
					value: 302,
					label: '302',
					color: 'rgba(23,161,229,.9)',
				},
				{
					value: 303,
					label: '303',
					color: 'rgba(23,161,229,.8)',
				},
				{
					value: 304,
					label: '304',
					color: 'rgba(23,161,229,.7)',
				},
				{
					value: 305,
					label: '305',
					color: 'rgba(23,161,229,.6)',
				},
				{
					value: 306,
					label: '306',
					color: 'rgba(23,161,229,.55)',
				},
				{
					value: 307,
					label: '307',
					color: 'rgba(23,161,229,.5)',
				},
				{
					value: 308,
					label: '308',
					color: 'rgba(23,161,229,.45)',
				},
				{
					value: 400,
					label: '400',
					color: 'rgba(255,152,48,.7)',
				},
				{
					value: 401,
					label: '401',
					color: 'rgba(255,152,48,.8)',
				},
				{
					value: 402,
					label: '402',
					color: 'rgba(255,152,48,.55)',
				},
				{
					value: 403,
					label: '403',
					color: 'rgba(255,152,48,.9)',
				},
				{
					value: 404,
					label: '404',
					color: '#FF9830',
				},
				{
					value: 405,
					label: '405',
					color: 'rgba(255,152,48,.6)',
				},
				{
					value: 406,
					label: '406',
					color: 'rgba(255,152,48,.5)',
				},
				{
					value: 407,
					label: '407',
					color: 'rgba(255,152,48,.45)',
				},
				{
					value: 408,
					label: '408',
					color: 'rgba(255,152,48,.4)',
				},
				{
					value: 409,
					label: '409',
					color: 'rgba(255,152,48,.35)',
				},
				{
					value: 410,
					label: '410',
					color: 'rgba(255,152,48,.3)',
				},
				{
					value: 411,
					label: '411',
					color: 'rgba(255,152,48,.25)',
				},
				{
					value: 412,
					label: '412',
					color: 'rgba(255,152,48,.2)',
				},
				{
					value: 413,
					label: '413',
					color: 'rgba(255,152,48,.15)',
				},
				{
					value: 414,
					label: '414',
					color: '#8595A6',
				},
				{
					value: 415,
					label: '415',
					color: '#8595A6',
				},
				{
					value: 416,
					label: '416',
					color: '#8595A6',
				},
				{
					value: 417,
					label: '417',
					color: '#8595A6',
				},
				{
					value: 418,
					label: '418',
					color: '#8595A6',
				},
				{
					value: 421,
					label: '421',
					color: '#8595A6',
				},
				{
					value: 422,
					label: '422',
					color: '#8595A6',
				},
				{
					value: 423,
					label: '423',
					color: '#8595A6',
				},
				{
					value: 424,
					label: '424',
					color: '#8595A6',
				},
				{
					value: 426,
					label: '426',
					color: '#8595A6',
				},
				{
					value: 428,
					label: '428',
					color: '#8595A6',
				},
				{
					value: 429,
					label: '429',
					color: '#8595A6',
				},
				{
					value: 431,
					label: '431',
					color: '#8595A6',
				},
				{
					value: 451,
					label: '451',
					color: '#8595A6',
				},
				{
					value: 500,
					label: '500',
					color: '#FF5959',
				},
				{
					value: 501,
					label: '501',
					color: 'rgba(255,89,89,.9)',
				},
				{
					value: 502,
					label: '502',
					color: 'rgba(255,89,89,.8)',
				},
				{
					value: 503,
					label: '503',
					color: 'rgba(255,89,89,.7)',
				},
				{
					value: 504,
					label: '504',
					color: 'rgba(255,89,89,.6)',
				},
				{
					value: 505,
					label: '505',
					color: 'rgba(255,89,89,.5)',
				},
				{
					value: 506,
					label: '506',
					color: 'rgba(255,89,89,.45)',
				},
				{
					value: 507,
					label: '507',
					color: 'rgba(255,89,89,.4)',
				},
				{
					value: 508,
					label: '508',
					color: 'rgba(255,89,89,.35)',
				},
				{
					value: 510,
					label: '510',
					color: 'rgba(255,89,89,.3)',
				},
				{
					value: 511,
					label: '511',
					color: 'rgba(255,89,89,.25)',
				},
			];

			const allCategories = useBrackets ? bracketCategories : specificCategories;
			return allCategories.filter((category) => {
				const item = chartData.find((dataItem) => dataItem.value === category.value);
				return item && item.count > 0;
			});
		},
		[
			useBrackets,
			chartData,
		],
	);

	return (
		<DonutPagesChart
			categories={categories}
			data={chartData}
			name="status_code"
			percentageFactor={100}
		/>
	);
};



export default React.memo(StatusCodeChart);
